import { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "./store/store";
import Auth from "./pages/auth";
import Dashboard from "./pages/dashboard";
import Home from "./pages/home";
import Players from "./pages/players";
import Representers from "./pages/representers";
import Tournaments from "./pages/tournaments";
import { ToastContainer, toast } from "react-toastify";
import { clearAlert } from "./store/slices/generalSlice";
import RepresenterDetail from "./pages/representer-detail";
import PlayerDetailPage from "./pages/player-detail";
import TournamentDetailPage from "./pages/tournament-detail";
import TournamentCategoryDetail from "./pages/tournament-category-detail";
import TeamDetail from "./pages/team-detail";
import AddTournament from "./pages/add-tournament";
import AddCategory from "./pages/add-category";
import TournamentCategories from "./pages/tournament-categories";
import TournamentStageOverview from "./pages/tournament-stage-overview";
import CreateSchedule from "./pages/create-schedule";
import MatchDetail from "./pages/match-detail";
import UserManagement from "./pages/user-management";
import TournamentPromoCodesPage from "./pages/tournament-promo-codes";
import TournamentAccommodations from "./pages/tournament-accommodations";
import TournamentGalleryPage from "./pages/tournament-gallery";
import Galleries from "./pages/galleries";
import CategoryCustomFieldsPage from "./pages/category-custom-fields";
import CourtReservations from "./pages/court-reservations";
import FastTournament from "./pages/fast-tournament";
import Configuration from "./pages/configuration";
import Transactions from "./pages/transactions";
import Reports from "./pages/reports";
import Announcements from "./pages/announcements";
import Events from "./pages/events";
import Partners from "./pages/partners";
import ShopProducts from "./pages/shop/products";
import ShopCatalogSetup from "./pages/shop/catalog";
import ShopOrders from "./pages/shop/orders";
import EventEditor from "./pages/events/EventEditor";
import UiKit from "./pages/ui-kit";
import { ProtectedRoute } from "./components";
import { UiProvider } from "./ui";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import { getToken, removeToken } from "./utils/token";
import { storeToken, setCurrentUser, logout, setLoading } from "./store/slices/userSlice";
import { getCurrentUser } from "./api/auth";
import {
  hasAdminAccess,
  getDefaultRoute,
  canAccessHome,
  canAccessPlayers,
  canAccessRepresenters,
  canAccessUserManagement,
  canAccessTournaments,
  canManageTournaments,
  canAccessGalleries,
  canManageCategories,
  canAccessCourtReservations,
  canAccessConfiguration,
  canAccessTransactions,
  canAccessReports,
  isHeadCoach,
  isSuperAdmin,
  canManagePartners,
  canManageShop,
} from "./utils/authorization";
import { AdminRole } from "./types/adminRoles";

function App() {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const isLoading = useAppSelector((state) => state.auth.isLoading);
  const { alertMessage, alertType } = useAppSelector((state) => state.general);
  const [isInitialized, setIsInitialized] = useState(false);

  const lastVisitedPage = useAppSelector((state) => {
    const page = state.navigation.lastVisitedPage;
    // Ensure the last visited page is a valid dashboard route
    if (page && page.startsWith("/dashboard")) {
      console.log("Redirecting to last visited page:", page);
      return page;
    }
    console.log("No valid last visited page, using default route");
    return getDefaultRoute(currentUser);
  });

  useEffect(() => {
    if (alertMessage) {
      if (alertType === "error") toast.error(alertMessage, { onClose: () => dispatch(clearAlert()) });
      else if (alertType === "success") toast.success(alertMessage, { onClose: () => dispatch(clearAlert()) });
    }
  }, [alertMessage, alertType, dispatch]);

  useEffect(() => {
    document.title = "The Clash Egypt - Admin Dashboard";
  }, []);

  // Fetch user details on app load if token exists
  useEffect(() => {
    const initializeAuth = async () => {
      const token = getToken();
      if (token) {
        dispatch(storeToken(token));
        dispatch(setLoading(true));
        try {
          const res = await getCurrentUser();
          const user = res.data;

          // Check if user has admin access (excluding referee admin only users) or is head coach
          if (!hasAdminAccess(user) && !isHeadCoach(user)) {
            dispatch(logout());
            removeToken();

            // Check if they only have referee admin role for specific message
            const hasOnlyRefereeAdmin = user.adminRoles?.every((role: AdminRole) => role === AdminRole.REFEREE_ADMIN);
            if (hasOnlyRefereeAdmin) {
              toast.error("Access denied. Referee admin role does not have dashboard access.");
            } else {
              toast.error("Access denied. You don't have permission to access the dashboard.");
            }
          } else {
            dispatch(setCurrentUser(user));
          }
        } catch (error: any) {
          console.error("Failed to fetch user:", error);
          dispatch(logout());
          removeToken();
        } finally {
          dispatch(setLoading(false));
          setIsInitialized(true);
        }
      } else {
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, [dispatch]);

  // Show loading spinner while initializing
  if (!isInitialized || (isAuthenticated && isLoading)) {
    return (
      <div className="app-loader-container">
        <div className="app-loader" />
      </div>
    );
  }

  return (
    <UiProvider>
    <Router>
      <Routes>
        <Route path="/" element={isAuthenticated ? <Navigate to={lastVisitedPage} /> : <Auth />} />
        {isAuthenticated && (
          <Route path="/dashboard" element={<Dashboard />}>
            {process.env.NODE_ENV === "development" && <Route path="ui-kit" element={<UiKit />} />}
            <Route
              index
              element={
                <ProtectedRoute canAccess={canAccessHome}>
                  <Home />
                </ProtectedRoute>
              }
            />
            <Route
              path="add-tournament"
              element={
                <ProtectedRoute canAccess={canManageTournaments}>
                  <AddTournament />
                </ProtectedRoute>
              }
            />
            <Route
              path="players"
              element={
                <ProtectedRoute canAccess={canAccessPlayers}>
                  <Players />
                </ProtectedRoute>
              }
            />
            <Route
              path="players/:id"
              element={
                <ProtectedRoute canAccess={canAccessPlayers}>
                  <PlayerDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="representers"
              element={
                <ProtectedRoute canAccess={canAccessRepresenters}>
                  <Representers />
                </ProtectedRoute>
              }
            />
            <Route
              path="representers/:id"
              element={
                <ProtectedRoute canAccess={canAccessRepresenters}>
                  <RepresenterDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="user-management"
              element={
                <ProtectedRoute canAccess={canAccessUserManagement}>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="tournaments"
              element={
                <ProtectedRoute canAccess={canAccessTournaments}>
                  <Tournaments />
                </ProtectedRoute>
              }
            />
            <Route
              path="tournaments/:id"
              element={
                <ProtectedRoute canAccess={canAccessTournaments}>
                  <TournamentDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="tournaments/:id/promo-codes"
              element={
                <ProtectedRoute canAccess={canManageTournaments}>
                  <TournamentPromoCodesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="galleries"
              element={
                <ProtectedRoute canAccess={canAccessGalleries}>
                  <Galleries />
                </ProtectedRoute>
              }
            />
            <Route
              path="tournaments/:id/gallery"
              element={
                <ProtectedRoute canAccess={canAccessGalleries}>
                  <TournamentGalleryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="tournaments/:id/accommodations"
              element={
                <ProtectedRoute canAccess={canManageTournaments}>
                  <TournamentAccommodations />
                </ProtectedRoute>
              }
            />
            <Route
              path="tournaments/:id/categories"
              element={
                <ProtectedRoute canAccess={canAccessTournaments}>
                  <TournamentCategories />
                </ProtectedRoute>
              }
            />
            <Route
              path="tournaments/:id/:tournamentCategoryId"
              element={
                <ProtectedRoute canAccess={canAccessTournaments}>
                  <TournamentCategoryDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="tournaments/matches/:matchId"
              element={
                <ProtectedRoute canAccess={canAccessTournaments}>
                  <MatchDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="tournaments/:id/:tournamentCategoryId/stages/:stageId"
              element={
                <ProtectedRoute canAccess={canAccessTournaments}>
                  <TournamentStageOverview />
                </ProtectedRoute>
              }
            />
            <Route
              path="tournaments/:id/:tournamentCategoryId/stages/:stageId/create-schedule"
              element={
                <ProtectedRoute canAccess={canManageTournaments}>
                  <CreateSchedule />
                </ProtectedRoute>
              }
            />
            <Route
              path="tournaments/:id/add-category"
              element={
                <ProtectedRoute canAccess={canManageCategories}>
                  <AddCategory />
                </ProtectedRoute>
              }
            />
            <Route
              path="tournaments/:id/:tournamentCategoryId/teams/:teamId"
              element={
                <ProtectedRoute canAccess={canAccessTournaments}>
                  <TeamDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="tournaments/:id/:tournamentCategoryId/custom-fields"
              element={
                <ProtectedRoute canAccess={canManageTournaments}>
                  <CategoryCustomFieldsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="court-reservations"
              element={
                <ProtectedRoute canAccess={canAccessCourtReservations}>
                  <CourtReservations />
                </ProtectedRoute>
              }
            />
            <Route
              path="fast-tournament"
              element={
                <ProtectedRoute canAccess={canAccessTournaments}>
                  <FastTournament />
                </ProtectedRoute>
              }
            />
            <Route
              path="configuration"
              element={
                <ProtectedRoute canAccess={canAccessConfiguration}>
                  <Configuration />
                </ProtectedRoute>
              }
            />
            <Route
              path="transactions"
              element={
                <ProtectedRoute canAccess={canAccessTransactions}>
                  <Transactions />
                </ProtectedRoute>
              }
            />
            <Route
              path="reports"
              element={
                <ProtectedRoute canAccess={canAccessReports}>
                  <Reports />
                </ProtectedRoute>
              }
            />
            <Route
              path="announcements"
              element={
                <ProtectedRoute canAccess={canManageTournaments}>
                  <Announcements />
                </ProtectedRoute>
              }
            />
            <Route
              path="partners"
              element={
                <ProtectedRoute canAccess={canManagePartners}>
                  <Partners />
                </ProtectedRoute>
              }
            />
            <Route
              path="shop/catalog"
              element={
                <ProtectedRoute canAccess={canManageShop}>
                  <ShopCatalogSetup />
                </ProtectedRoute>
              }
            />
            <Route
              path="shop/products"
              element={
                <ProtectedRoute canAccess={canManageShop}>
                  <ShopProducts />
                </ProtectedRoute>
              }
            />
            <Route
              path="shop/orders"
              element={
                <ProtectedRoute canAccess={canManageShop}>
                  <ShopOrders />
                </ProtectedRoute>
              }
            />
            <Route path="shop" element={<Navigate to="/dashboard/shop/orders" replace />} />
            <Route
              path="events"
              element={
                <ProtectedRoute canAccess={isSuperAdmin}>
                  <Events />
                </ProtectedRoute>
              }
            />
            <Route
              path="events/new"
              element={
                <ProtectedRoute canAccess={isSuperAdmin}>
                  <EventEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="events/:id/edit"
              element={
                <ProtectedRoute canAccess={isSuperAdmin}>
                  <EventEditor />
                </ProtectedRoute>
              }
            />
          </Route>
        )}

        <Route path="*" element={<Navigate to={isAuthenticated ? lastVisitedPage : "/"} />} />
      </Routes>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme="dark"
        toastStyle={{
          backgroundColor: "white",
          color: "black",
          fontSize: "12px",
          borderRadius: "8px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
        }}
      />
    </Router>
    </UiProvider>
  );
}

export default App;
